import type { CalendarDate, CalendarDateTime, DateValue, ZonedDateTime } from "@internationalized/date";
import { DateFieldInput, DateFieldRoot } from "reka-ui";
import { defineComponent } from "vue";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";

type DateFieldValue = CalendarDate | CalendarDateTime | ZonedDateTime;

const Root = defineComponent({
  name: "DateField",
  props: {
    modelValue: p.custom<DateFieldValue | undefined>([Object]),
    defaultValue: p.custom<DateFieldValue | undefined>([Object]),
    placeholder: p.custom<DateFieldValue | undefined>([Object]),
    locale: p.string("en-US"),
    granularity: p.string<"day" | "hour" | "minute" | "second">("day"),
    disabled: p.boolean(false),
    readonly: p.boolean(false),
    minValue: p.custom<DateFieldValue | undefined>([Object]),
    maxValue: p.custom<DateFieldValue | undefined>([Object]),
  },
  emits: {
    "update:modelValue": (_value: DateValue) => true,
  },
  setup(props, { emit, attrs }) {
    return () => (
      <DateFieldRoot
        {...attrs}
        class={cn(
          "border-input flex h-8 w-full items-center rounded-md border bg-transparent px-3 py-1 text-sm transition-colors",
          "hover:border-accent-10",
          "focus-within:border-accent-11",
          props.disabled && "cursor-not-allowed opacity-50",
          attrs.class as string,
        )}
        modelValue={props.modelValue}
        defaultValue={props.defaultValue}
        placeholder={props.placeholder}
        locale={props.locale}
        granularity={props.granularity}
        disabled={props.disabled}
        readonly={props.readonly}
        minValue={props.minValue}
        maxValue={props.maxValue}
        onUpdate:modelValue={(value: DateValue | undefined) => {
          if (value) emit("update:modelValue", value);
        }}
        v-slots={{
          default: ({ segments }: { segments: { part: string; value: string }[] }) => (
            <>
              {segments.map((segment, idx) =>
                segment.part === "literal" ? (
                  <span key={idx} class="text-muted-foreground">
                    {segment.value}
                  </span>
                ) : (
                  <DateFieldInput
                    key={idx}
                    part={segment.part as "day" | "month" | "year" | "hour" | "minute" | "second" | "dayPeriod" | "timeZoneName"}
                    class="focus:bg-accent-3 focus:text-accent-11 rounded-sm px-0.5 tabular-nums focus:outline-none"
                  >
                    {segment.value}
                  </DateFieldInput>
                ),
              )}
            </>
          ),
        }}
      />
    );
  },
});

export default {
  Root,
};
